import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import StringUtils from '~/common/string-utils';
import AssetPickerTypes, { NonAssetPickerTypes } from '~/components/common/asset-picker/asset-picker-types';
import AcademyThemeContent from '../common/academy-theme-content';
import AcademyThemeContentCard from '../common/academy-theme-content-card';
import AcademyThemeCardWrapper from '../common/academy-theme-card-wrapper';
import withAcademyTheme from '../common/with-academy-theme';
import AcademyContentFilters from '../common/academy-content-filters';
import AcademyNoContent from './academy-no-content';
import { AcademyLearnerCollectionsList } from './collections-content/academy-learner-collections-list';

class AcademyLearnerHomeInner extends Component {
  TRANSLATED_NO_CONTENT = I18n.t('js.academy.home.no_content');

  TRANSLATED_NO_FILTERED_CONTENT = I18n.t('js.academy.home.no_filtered_content');

  TRANSLATED_SEARCHING = I18n.t('js.academy.home.searching');

  TRANSLATED_FEATURED = I18n.t('js.academy.home.featured');

  TRANSLATED_ALL = I18n.t('js.academy.home.all');

  state = {
    filteredContent: [], // comes from AcademyContentFilters
    filtered: false,
    filtering: false,
  };

  renderContentFilters = (academy) => (
    <AcademyContentFilters
      academyId={academy.id}
      activeColor={academy.themeOptions.accentColor}
      align="right"
      filterHeight={200}
      filterLabels={{
        tags: 'Filter',
      }}
      filters={[
        {
          options: _.map(this.props.guideAndResourceTags, (tag) => tag.name),
          type: 'tags',
        },
      ]}
      validContentTypes={[
        AssetPickerTypes.GUIDE,
        AssetPickerTypes.RESOURCE,
        AssetPickerTypes.SCORM,
        NonAssetPickerTypes.ACADEMY_COLLECTION_CONTENT,
        NonAssetPickerTypes.ACADEMY_CERTIFICATION_CONTENT,
      ]}
      renderResults={(results) => this.setState({ filteredContent: results })}
      style={{
        marginBottom: '25px',
      }}
      updateFilteredState={(filtered) => this.setState({ filtered })}
      updateFilteringState={(filtering) => this.setState({ filtering })}
    />
  );

  renderFilteredContent = () => {
    const { academy } = this.props;

    const allText = StringUtils.getSectionLabelOverride({
      contentLabelOverrides: academy.themeOptions.contentLabelOverrides,
      section: 'Trainings',
      label: APP_CONSTANTS.ACADEMY_LABEL_OVERRIDABLE_SECTION_KEY.ALL,
      overrideDefault: this.TRANSLATED_ALL,
    });

    const filteredCollectionIds = [];
    this.state.filteredContent.forEach((item) => {
      if (!item.searchResult) {
        console.log('Unknown result format:', item);
        return;
      }

      const {
        searchResult: { content_type: contentType, parent_id: parentId, parent_type: parentType },
      } = item;

      if (parentType === AssetPickerTypes.ACADEMY_COLLECTION) {
        switch (contentType) {
          case AssetPickerTypes.GUIDE:
          case AssetPickerTypes.RESOURCE:
          case AssetPickerTypes.SCORM:
            if (filteredCollectionIds.indexOf(parentId) === -1) {
              filteredCollectionIds.push(parentId);
            }
            break;

          default:
            console.log('Unknown content_type:', contentType);
            break;
        }
      } else {
        console.log('Unknown parent_type:', parentType);
      }
    });

    const sortedCollections = _.sortBy(
      academy.academyCollections.filter((collection) => filteredCollectionIds.indexOf(collection.id) >= 0),
      (collection) => collection.uiOrder,
    );

    return (
      <AcademyLearnerCollectionsList
        showHeader={false}
        collections={sortedCollections}
        header={allText}
        themeOptions={academy.themeOptions}
        collectionCardVersion={academy.themeOptions.collectionCardVersion}
      />
    );
  };

  renderErrorMsg = (errorMsg) => (
    <ErrorMsgWrapper>
      <AcademyNoContent msg={errorMsg} />
    </ErrorMsgWrapper>
  );

  renderNoContent = () => <AcademyNoContent msg={this.TRANSLATED_NO_CONTENT} />;

  renderNoFilteredContent = () => (
    <AcademyThemeCardWrapper>
      <AcademyNoContent msg={this.TRANSLATED_NO_FILTERED_CONTENT} />
    </AcademyThemeCardWrapper>
  );

  renderFilteringContent = () => <AcademyNoContent msg={this.TRANSLATED_SEARCHING} />;

  render() {
    const { academy, showFilters, guideAndResourceTags, errorMsg, featuredAcademyCollections } = this.props;
    if (errorMsg) return this.renderErrorMsg(errorMsg);

    const { filtering, filteredContent, filtered } = this.state;

    const contentStyle = {
      paddingBottom: '20px',
    };

    if (showFilters) {
      contentStyle.paddingTop = '20px';
    }

    if (filtering) {
      return (
        <AcademyThemeContent style={contentStyle}>
          {showFilters && !_.isEmpty(guideAndResourceTags) && this.renderContentFilters(academy)}
          <AcademyThemeCardWrapper>{this.renderFilteringContent()}</AcademyThemeCardWrapper>
        </AcademyThemeContent>
      );
    }

    if (filtered) {
      return (
        <AcademyThemeContent style={contentStyle}>
          {showFilters && !_.isEmpty(guideAndResourceTags) && this.renderContentFilters(academy)}
          {!_.isEmpty(filteredContent) ? this.renderFilteredContent() : this.renderNoFilteredContent()}
        </AcademyThemeContent>
      );
    }

    if (!academy.academyCollections || academy.academyCollections.length === 0) {
      return (
        <AcademyThemeContent style={contentStyle}>
          {showFilters && !_.isEmpty(guideAndResourceTags) && this.renderContentFilters(academy)}
          <AcademyThemeCardWrapper>{this.renderNoContent()}</AcademyThemeCardWrapper>
        </AcademyThemeContent>
      );
    }

    const sortedCollections = _.sortBy(academy.academyCollections, (collection) => collection.uiOrder);
    const featuredCollections = sortedCollections.filter((collection) => collection.isFeatured);
    const hasFeaturedContent = featuredCollections && featuredCollections.length !== 0;

    const featuredText = StringUtils.getSectionLabelOverride({
      contentLabelOverrides: academy.themeOptions.contentLabelOverrides,
      section: 'Trainings',
      label: APP_CONSTANTS.ACADEMY_LABEL_OVERRIDABLE_SECTION_KEY.FEATURED_SECTION,
      overrideDefault: this.TRANSLATED_FEATURED,
    });
    const allText = StringUtils.getSectionLabelOverride({
      contentLabelOverrides: academy.themeOptions.contentLabelOverrides,
      section: 'Trainings',
      label: APP_CONSTANTS.ACADEMY_LABEL_OVERRIDABLE_SECTION_KEY.ALL,
      overrideDefault: this.TRANSLATED_ALL,
    });

    return (
      <AcademyThemeContent style={contentStyle}>
        {showFilters && !_.isEmpty(guideAndResourceTags) && this.renderContentFilters(academy)}
        <>
          {hasFeaturedContent && (
            <AcademyLearnerCollectionsList
              showHeader
              collections={featuredCollections}
              header={featuredText}
              themeOptions={academy.themeOptions}
              collectionCardVersion={academy.themeOptions.collectionCardVersion}
            />
          )}
          <AcademyLearnerCollectionsList
            showHeader={hasFeaturedContent}
            collections={sortedCollections}
            header={allText}
            themeOptions={academy.themeOptions}
            collectionCardVersion={academy.themeOptions.collectionCardVersion}
          />
        </>
      </AcademyThemeContent>
    );
  }
}

const ErrorMsgWrapper = styled.div`
  position: relative;
  background-color: #fff;
  min-height: 100vh;
  box-sizing: border-box;
  width: 80%;
  padding-left: 20%;
  padding-top: 10%;
`;

const AcademyLearnerHome = withAcademyTheme(AcademyLearnerHomeInner);

export default AcademyLearnerHome;
